import styled from 'styled-components';
import { Table as AntdTable } from 'antd';
import { zPositions } from '@/constants';
import { Color, Color as NewColor } from '@bb/elements-web';
import { Button } from '../Button';
import { Dropdown } from '../Dropdown';
import { Text } from '../Typography';
import theme from '../theme';

export const StyledUniversalSearch = styled.div<{
  isInputFocused: boolean;
  orgType: string;
  isAssumedNotBB?: boolean;
  assumerIdMlsStaff?: string;
  offsetTop: number;
  xl: boolean;
  siderWidth: number;
  searchFromTour?: boolean;
  hasShowingsMadeEasyFeature?: boolean;
}>`
  ${({
    isInputFocused,
    orgType,
    offsetTop,
    xl,
    siderWidth,
    isAssumedNotBB,
    assumerIdMlsStaff,
    hasShowingsMadeEasyFeature,
  }) => {
    const totalOffsetTop = (offsetTop + 9).toString();
    const offsetTopString = offsetTop.toString();
    const rightOffset = hasShowingsMadeEasyFeature ? '328px' : '8%';
    return (
      (orgType === 'brokerage' || !(isAssumedNotBB || assumerIdMlsStaff)) &&
      isInputFocused &&
      `
      position: fixed;
      width: ${
        !xl
          ? `100vw`
          : `calc(100% - ${siderWidth.toString()}px - ${rightOffset})`
      };
      max-width: 1200px;
      top: ${!xl ? `${offsetTopString}px` : `${totalOffsetTop}px`};
      ${!xl ? `left: 0px;` : ''}
      z-index: ${zPositions.universalSearch};
      border-radius: 4px;
        box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05),
          0px 6px 16px 0px rgba(0, 0, 0, 0.08),
          0px 3px 6px -4px rgba(0, 0, 0, 0.12);
      background-color: ${theme.gray1};
    `
    );
  }}
  margin-left: 8px;
  max-width: 1200px;
  :hover {
    border-radius: 4px;
    ${({ isInputFocused, orgType }) =>
      orgType === 'brokerage' &&
      !isInputFocused &&
      `
        background-color: ${theme.gray3};
        cursor: text;
      `}
  }
  > .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  > div > span {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    border: none;
    border-radius: 4px;
    box-shadow: none;
    .icon-wrapper {
      width: 30px;
      display: flex;
      .spin-icon {
        margin-left: 5px;
        margin-bottom: 5px;
      }
    }
    &,
    input {
      background-color: ${({ hasShowingsMadeEasyFeature, searchFromTour }) =>
        hasShowingsMadeEasyFeature && !searchFromTour
          ? Color.WHITE
          : Color.TRANSPARENT};
    }
    .ant-input {
      max-width: 500px;
      height: 33px;
    }
    ${({ hasShowingsMadeEasyFeature }) =>
      hasShowingsMadeEasyFeature &&
      `.ant-input::placeholder {
          color: ${Color.BLACK};
          opacity: 0.65;
      }
  `}
  }
  ${({ searchFromTour, isInputFocused, offsetTop }) =>
    searchFromTour &&
    `
    ${
      isInputFocused &&
      'box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);'
    }

    top: ${offsetTop.toString()}px;
    > .top-row {
      height: 33px;
      margin-top: 8px;
    }
    > div > span {
      background-color: ${isInputFocused ? 'transparent' : '#F0F0F0'};
      width: 100%;
      align-items: center;
      .icon-wrapper {
        width: 22px;
      }
      .ant-input {
        font-size: 14px;
        max-width: none;
        width: 100%;
      }
    }
  `}
`;

export const StyledTable = styled(AntdTable)<{
  lg?: boolean;
  md?: boolean;
  searchFromTour?: boolean;
  customOffset?: number;
  hasShowingsMadeEasyFeature?: boolean;
}>`
  > div {
    overflow: auto;
    max-height: ${({ customOffset }) =>
      customOffset
        ? `calc(100vh - 150px - ${customOffset}px)`
        : 'calc(100vh - 150px)'};
  }
  table {
    width: 100%;
    max-width: 100vw;
    tbody {
      tr {
        vertical-align: middle;
        border-top: 1px solid ${theme.gray3};
        transition: background-color 0.5s;
        &:hover td {
          background: initial;
        }
        &.active {
          background-color: ${theme.gray3};
        }
        td {
          vertical-align: middle;
          &.ellipsis {
            padding: 5px 0;
            overflow: hidden;
            text-overflow: ellipsis;

            * {
              text-overflow: ellipsis;
              display: block;
              overflow: hidden;
              width: 100%;
            }
          }
        }
      }
    }
  }
  .ant-table-tbody > tr > td.cross-org,
  .ant-table-tbody > tr > td.org-column,
  .ant-table-tbody > tr > td.agents-column,
  .ant-table-tbody > tr > td.appointment-desk-agent-column,
  .ant-table-tbody > tr > td.action-buttons-column,
  .ant-table-tbody > tr > td.crossOrg-button-column,
  .ant-table-tbody > tr > td.stop-thumbnail-column,
  .ant-table-tbody > tr > td.stop-address-column {
    border: none;
  }
  .ant-table-tbody > tr > td.tag-column {
    width: 20px;
    padding: 0;
  }
  .ant-table-tbody > tr > td.tick-column {
    margin-right: 8px;
  }

  &.listing {
    .eye-column {
      width: 70px;
      max-width: 70px;
      text-align: center;
      button {
        padding: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        > * {
          width: 100%;
        }
      }
    }
    .address-link {
      white-space: normal;
      height: auto;
      color: ${theme.getColor('primary')};
    }
    .address-column {
      // additional class for this column for cross org listing search table
      &.cross-org {
        width: 25%;
        ${({ md, searchFromTour }) => !md && searchFromTour && `width: 80%;`}
      }
      button,
      a {
        text-align: left;
      }
    }
    .price-column {
      max-width: 150px;
    }
    .status-column {
      max-width: 200px;
    }
    .availability-column {
      max-width: 150px;
    }
    .agent-column {
      max-width: 300px;
    }
    // next 3 columns are only in cross org listing search
    .org-column {
      ${({ lg, searchFromTour }) =>
        // eslint-disable-next-line no-nested-ternary
        lg
          ? searchFromTour
            ? `width: 25%; max-width: 250px;`
            : `width: 20%; min-width: 140px;`
          : `display: none;`}
    }
    .agents-column {
      ${({ lg, searchFromTour }) =>
        // eslint-disable-next-line no-nested-ternary
        lg
          ? searchFromTour
            ? `width: 21%; max-width: 180px;`
            : `width: 14%; max-width: 160px;`
          : `display: none;`}
      >:not(:first-child) {
        margin-top: 10px;
      }
    }
    .appointment-desk-agent-column {
      width: 18%;
    }
    .action-buttons-column {
      ${({ searchFromTour, md, hasShowingsMadeEasyFeature }) =>
        searchFromTour
          ? !md
            ? 'max-width: 44px'
            : 'min-width: 180px'
          : !hasShowingsMadeEasyFeature && 'min-width: 280px'};
      > * {
        display: flex;
        justify-content: flex-end;
      }
    }
    &.cross-org {
      .ant-table-row {
        &:hover {
          cursor: pointer;
        }
        .ant-table-cell {
          background: unset;
        }
      }
    }
  }

  &.agent {
    &.cross-org {
      .ant-table-row {
        .ant-table-cell {
          background: unset;
        }
      }
    }
  }

  &&.org {
    .image-column {
      width: 62px;
      padding: 15px;
      .image-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 1px solid #f0f0f0;
        > img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }

  &.stop {
    .ant-table-row {
      &:hover {
        cursor: pointer;
      }
      .ant-table-cell {
        background: unset;
      }
    }

    .stop-thumbnail-column {
      width: 72px;
      max-width: 72px;
      & > div {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .action-buttons-column {
      > * {
        display: flex;
        justify-content: flex-end;
      }
    }
  }
`;

export const StyledAddressName = styled(Text)`
  font-size: 12px;
  color: ${theme.gray7};
  text-align: left;
  text-overflow: ellipsis;
`;

export const StyledBrokerageFilter = styled.div`
  margin-right: 10px;
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .ant-select-open .ant-select-selection-item {
    color: #5c93fd;
  }
  .ant-select-item-option-active {
    background-color: ${theme.gray1};
    :hover {
      background-color: ${theme.gray3};
    }
  }
  .ant-select-item-option-selected {
    font-weight: 400;
    background-color: ${theme.gray3};
  }
`;

export const StyledSearchResults = styled.div<{ $searchFromTour: boolean }>`
  background-color: ${theme.gray1};
  ${({ $searchFromTour }) =>
    $searchFromTour ? 'border-radius: 0 0 13px 13px;' : 'border-radius: 4px;'}
  .ant-tabs {
    width: 100%;
    ${({ $searchFromTour }) =>
      $searchFromTour && 'border-radius: 0 0 13px 13px;'}
  }
`;

export const StyledRecentsHeading = styled(Text)`
  width: 100%;
  margin: 16px 8px;
  line-height: 22px;
`;

export const StyledClearRecentsButton = styled(Button)<{
  $hasSupraRebrandingFeature?: boolean;
}>`
  position: absolute;
  top: 53px;
  right: 5px;
  font-weight: 400;
  ${({ $hasSupraRebrandingFeature }) =>
    $hasSupraRebrandingFeature && `color: ${NewColor.PRIMARY}`};
`;

export const StyledButton = styled(Button)<{
  disabled?: boolean;
  $searchFromTour?: boolean;
}>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
  :disabled > .anticon {
    color: ${theme.gray5};
  }
  ${({ $searchFromTour }) =>
    $searchFromTour &&
    `
  border-radius: 50px;
`}
`;

export const StyledButtonWrapper = styled.div<{
  disabled?: boolean;
  searchFromTour?: boolean;
  md?: boolean;
}>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  ${({ searchFromTour, md }) =>
    searchFromTour &&
    `
    margin-right: ${md ? 20 : 0}px;
    font-weight: 400;
  `}
`;

export const NoWrapRow = styled.div<{ external?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  margin: 1px 0;
  span {
    font-size: 90%;
  }
  ${({ external }) => external && 'padding-right: 10px;'}
`;

export const WrapRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export const NoWrapText = styled(Text)`
  white-space: nowrap;
`;

export const DotIcon = styled.div`
  display: inline-block;
  width: 3px;
  height: 3px;
  background: #000;
  border-radius: 50%;
  margin: 0 5px;
`;

export const StyledAvailability = styled(Text)<{
  availability?: string;
  $hasSupraRebrandingFeature?: boolean;
}>`
  white-space: nowrap;
  color: ${({ availability, $hasSupraRebrandingFeature }) => {
    switch (availability) {
      case 'New':
      case 'Extended':
      case 'Price Change':
        return $hasSupraRebrandingFeature ? NewColor.PRIMARY : theme.blue6;
      case 'Sold':
      case 'Leased':
        return theme.green6;
      case 'Sold Conditionally':
      case 'Leased Conditionally':
        return theme.warningColor;
      default:
        return theme.textColorSecondary;
    }
  }};
`;

export const StyledFlexWrapper = styled.div<{ $justifyContent?: string }>`
  padding: 0 10px;
  display: flex;
  ${props =>
    props.$justifyContent && `justify-content: ${props.$justifyContent};`}
`;

export const StyledMlsName = styled(Text)`
  width: fit-content;
`;

export const StyledDropdown = styled(Dropdown)`
  position: fixed;
  .ant-dropdown-menu {
    min-width: 148px;
  }
  .ant-dropdown-menu-item {
    gap: 8px;
    padding: 8px 16px;
  }
`;

export const StyledModalTitle = styled.h3`
  display: flex;
  font-weight: 500;
  margin: 0dvb;
`;

export const StyledModalContent = styled.div`
  display: flex;
  font-size: 14px;
  text-align: left;
  text-wrap: auto;
`;

export const StyledModalButtonText = styled.div`
  font-size: 14px;
`;
